import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Table, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { FaCheck, FaTimes, FaArrowLeft } from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';

const StaffPermissions = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState('');
  const [updatingPermissionId, setUpdatingPermissionId] = useState(null);
  const [search, setSearch] = useState('');

  // Get JWT token from localStorage
  const getAuthToken = () => localStorage.getItem('authToken'); // Retrieve token from localStorage

  useEffect(() => {
    fetchStaff();
    fetchPermissions();
    // eslint-disable-next-line
  }, []);

  const fetchStaff = async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${appConstants.API_ROOT_URL}get-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: staffId }),
      });
      const data = await response.json();
      if (response.ok) setStaff(data.data);
      else setError(data.message || 'Failed to fetch staff details.');
    } catch (error) {
      setError('Failed to fetch staff details.');
      console.error('Failed to fetch staff details:', error);
    }
  };

  const fetchPermissions = async () => {
    const token = getAuthToken();
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-staff-permissions?staff_id=${staffId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setPermissions(response.data.permissions);
    } catch (error) {
      setError('Failed to fetch permissions.');
      console.error('Failed to fetch permissions', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = async (permissionId, assigned) => {
    setUpdatingPermissionId(permissionId);
    setError('');
    const token = getAuthToken();
    // Optimistic UI update
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm) =>
        perm.id === permissionId ? { ...perm, assigned: !assigned } : perm,
      ),
    );
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-staff-permission`,
        {
          staff_id: staffId,
          permission_id: permissionId,
          assigned: !assigned,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.status !== 'success') {
        setError(response.data.message || 'Failed to update permission.');
        // Revert UI if failed
        setPermissions((prevPermissions) =>
          prevPermissions.map((perm) =>
            perm.id === permissionId ? { ...perm, assigned } : perm,
          ),
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'An error occurred while updating permission.';
      setError(errorMessage);
      // Revert UI if failed
      setPermissions((prevPermissions) =>
        prevPermissions.map((perm) =>
          perm.id === permissionId ? { ...perm, assigned } : perm,
        ),
      );
      console.error('Failed to update permission:', error);
    } finally {
      setUpdatingPermissionId(null);
    }
  };

  const capitalizeFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  // Filter permissions by search
  const filteredPermissions = permissions.filter((perm) =>
    perm.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Layout>
      <div className="container mt-4">
        {/* Top Bar with Search */}
        <div
          style={{
            background: '#343a40',
            color: '#fff',
            borderRadius: 8,
            padding: '0.7rem 1rem 0.7rem 1rem',
            marginBottom: 20,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: 0.5 }}>
              Permissions
            </div>
            <div style={{ fontSize: 16, fontWeight: 400, marginTop: 8 }}>
              <span style={{ color: '#ffc107', fontWeight: 500 }}>
                {staff?.name || '...'}
              </span>
              {staff?.designation && (
                <span style={{ color: '#adb5bd', marginLeft: 12 }}>
                  {staff.designation}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                minWidth: 260,
                background: 'white',
                borderRadius: 8,
              }}
            >
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search permission..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search permission"
                  style={{
                    borderRadius: '8px',
                    borderRight: search ? 'none' : undefined,
                  }}
                />
                {search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearch('')}
                    tabIndex={0}
                    aria-label="Clear search"
                    style={{ borderRadius: '0 8px 8px 0' }}
                  >
                    ×
                  </Button>
                )}
              </InputGroup>
            </div>
            <Button
              variant="outline-light"
              size="sm"
              style={{
                borderRadius: 6,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '38px',
              }}
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <FaArrowLeft />
            </Button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <Spinner animation="border" />
        ) : filteredPermissions.length === 0 ? (
          <div className="text-muted">
            No permissions found
            {search ? ' for this search.' : ' for this staff member.'}
          </div>
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Permission</th>
                <th>Assigned</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map((perm, index) => (
                <tr key={perm.id}>
                  <td>{index + 1}</td>
                  <td>{capitalizeFirstLetter(perm.name.replace(/_/g, ' '))}</td>
                  <td>
                    <Button
                      variant={perm.assigned ? 'success' : 'danger'}
                      size="sm"
                      aria-label={
                        perm.assigned
                          ? `Revoke ${perm.name}`
                          : `Grant ${perm.name}`
                      }
                      onClick={() => togglePermission(perm.id, perm.assigned)}
                      disabled={updatingPermissionId === perm.id}
                    >
                      {updatingPermissionId === perm.id ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : perm.assigned ? (
                        <FaCheck />
                      ) : (
                        <FaTimes />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Layout>
  );
};

export default StaffPermissions;
